import React, { useState, useEffect } from 'react';
import { useRegion } from '@/contexts/RegionContext';
import { pricingTable, bundleSaving, ProductPricing } from '@/lib/pricing';

export interface PriceDisplayProps {
  productKey: string;
  className?: string;
  showSaving?: boolean;
}

/**
 * Universal dynamic price display component enforcing regional rules:
 * - "za" (South Africa & Southern Africa):
 *     - If vat === true (South Africa): dual display "R18 000 excl VAT | R20 700 incl VAT"
 *     - If vat === false (NA, BW, LS, SZ, etc.): "R18 000 excl VAT"
 * - "uk" (UK & Ireland): "£1,350"
 * - "row" (Rest of World): "$1,800"
 *
 * During SSR and before client hydration, renders an empty placeholder so that
 * edge-cached server HTML (Cloudflare CDN-Cache-Control) contains no baked-in currency amounts.
 */
export const PriceDisplay: React.FC<PriceDisplayProps> = ({
  productKey,
  className = '',
}) => {
  const [mounted, setMounted] = useState(false);
  const { region, vatApplies } = useRegion();

  useEffect(() => {
    setMounted(true);
  }, []);

  // During SSR and pre-hydration, return an empty container
  if (!mounted) {
    return <span className={`inline-block min-h-[1.25em] ${className}`.trim()} aria-hidden="true" />;
  }

  const product: ProductPricing | undefined = pricingTable[productKey];
  if (!product) {
    return null;
  }

  if (region === 'za') {
    if (vatApplies) {
      return (
        <span className={`inline-flex flex-wrap items-center gap-x-2 text-inherit font-inherit ${className}`.trim()}>
          <span className="whitespace-nowrap">{product.zar.formattedExclVat} excl VAT</span>
          <span className="select-none opacity-40" aria-hidden="true">|</span>
          <span className="whitespace-nowrap">{product.zar.formattedInclVat} incl VAT</span>
        </span>
      );
    }

    return (
      <span className={`text-inherit font-inherit ${className}`.trim()}>
        <span className="whitespace-nowrap">{product.zar.formattedExclVat} excl VAT</span>
      </span>
    );
  }

  if (region === 'uk') {
    return (
      <span className={`text-inherit font-inherit ${className}`.trim()}>
        <span className="whitespace-nowrap">{product.gbp.formattedAmount}</span>
      </span>
    );
  }

  return (
    <span className={`text-inherit font-inherit ${className}`.trim()}>
      <span className="whitespace-nowrap">{product.usd.formattedAmount}</span>
    </span>
  );
};

export const SavingDisplay: React.FC<{ className?: string; prefix?: string }> = ({
  className = '',
  prefix = 'Save',
}) => {
  const [mounted, setMounted] = useState(false);
  const { region } = useRegion();

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <span className={`inline-block min-h-[1em] ${className}`.trim()} aria-hidden="true" />;
  }

  const amount =
    region === 'za'
      ? bundleSaving.zar.formattedExclVat
      : region === 'uk'
      ? bundleSaving.gbp.formattedAmount
      : bundleSaving.usd.formattedAmount;

  const text = prefix ? `${prefix} ${amount}` : amount;
  return <span className={className}>{text}</span>;
};

export default PriceDisplay;
